import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Switch } from './ui/switch';
import { Badge } from './ui/badge';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { listPedimentos, uploadPedimento, downloadPedimento, updatePedimento, deletePedimento, soiaLookup, extractPedimentoOnly, consultSoiaForShipment } from '../lib/api';
import { toast } from 'sonner';
import { Upload, FileText, Download, Trash2, Eye, EyeOff, Edit2, Loader2, Check, Hash, ExternalLink, Search, Clock, Plus, X, Zap, RefreshCw } from 'lucide-react';
import { useI18n } from '../contexts/I18nContext';
import { SoiaTimeline } from './SoiaTimeline';

const SOIA_URL = "https://aplicacionesc.mat.sat.gob.mx/SOIA_CR_WEB/oia_consultarap_cep.aspx";
const ADUANAS = [
  { code: "430", name: "Manzanillo" }, { code: "470", name: "Lazaro Cardenas" },
  { code: "800", name: "Cd. Mexico" }, { code: "810", name: "Aeropuerto CDMX" },
  { code: "820", name: "Pantaco" }, { code: "640", name: "Guadalajara" },
  { code: "240", name: "Nuevo Laredo" }, { code: "160", name: "Cd. Juarez" },
  { code: "480", name: "Queretaro" },
];

export function PedimentoPanel({ shipmentMbl, containers = [], isAdmin = false }) {
  const { t } = useI18n();
  const [pedimentos, setPedimentos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editData, setEditData] = useState({});
  const [uploadVisible, setUploadVisible] = useState(true);
  const [uploadContainerId, setUploadContainerId] = useState('');
  const fileInputRef = useRef(null);

  // Proforma (extract-only, discard PDF) state
  const [extracting, setExtracting] = useState(false);
  const [extractContainerId, setExtractContainerId] = useState('');
  const extractFileRef = useRef(null);

  // Manual "Consultar SOIA ahora" state
  const [consultingSoia, setConsultingSoia] = useState(false);

  const handleConsultSoiaNow = async () => {
    setConsultingSoia(true);
    const tid = toast.loading('Consultando SOIA (SAT)... esto puede tardar hasta 1 min');
    try {
      const res = await consultSoiaForShipment(shipmentMbl);
      toast.success(res.data?.message || 'Consulta SOIA completada', { id: tid, duration: 8000 });
      loadPedimentos();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al consultar SOIA', { id: tid });
    } finally {
      setConsultingSoia(false);
    }
  };

  // SOIA state
  const [soiaDialogOpen, setSoiaDialogOpen] = useState(false);
  const [soiaPedId, setSoiaPedId] = useState(null);
  const [soiaParsed, setSoiaParsed] = useState({ anio: '', aduana: '', patente: '', documento: '' });
  const [soiaMovimientos, setSoiaMovimientos] = useState([{ situacion: '', detalle: '', fecha: '' }]);

  const loadPedimentos = useCallback(async () => {
    try {
      setLoading(true);
      const res = await listPedimentos({ shipment_mbl: shipmentMbl });
      setPedimentos(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [shipmentMbl]);

  useEffect(() => { loadPedimentos(); }, [loadPedimentos]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.pdf')) { toast.error(t('common.pdfOnly')); return; }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const params = { shipment_mbl: shipmentMbl, visible_to_client: uploadVisible };
      if (uploadContainerId) params.container_id = uploadContainerId;
      const res = await uploadPedimento(formData, params);
      toast.success(res.data.pedimento_number ? `Pedimento subido. Num: ${res.data.pedimento_number}` : 'Pedimento subido');
      loadPedimentos();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error al subir'); }
    finally { setUploading(false); if (fileInputRef.current) fileInputRef.current.value = ''; }
  };

  const handleExtractOnly = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.pdf')) { toast.error(t('common.pdfOnly')); return; }
    setExtracting(true);
    const tid = toast.loading('Extrayendo número de proforma...');
    try {
      const formData = new FormData();
      formData.append('file', file);
      const params = { shipment_mbl: shipmentMbl };
      if (extractContainerId) params.container_id = extractContainerId;
      const res = await extractPedimentoOnly(formData, params);
      const d = res.data || {};
      toast.success(
        `Pedimento ${d.pedimento_number} asignado a ${d.total_containers_updated} contenedor(es). PDF descartado.`,
        { id: tid, duration: 7000 }
      );
      loadPedimentos();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No se pudo extraer el número de pedimento', { id: tid });
    } finally {
      setExtracting(false);
      if (extractFileRef.current) extractFileRef.current.value = '';
    }
  };

  const handleDownload = async (ped) => {
    try {
      const res = await downloadPedimento(ped.id);
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a'); a.href = url; a.download = ped.original_filename;
      document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
    } catch { toast.error(t('common.errorLoading')); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminar pedimento?')) return;
    try { await deletePedimento(id); toast.success(t('common.deletedOk')); loadPedimentos(); }
    catch { toast.error(t('common.errorDeleting')); }
  };

  const handleSaveEdit = async () => {
    if (!editId) return;
    try { await updatePedimento(editId, editData); toast.success(t('common.updatedOk')); setEditId(null); loadPedimentos(); }
    catch { toast.error(t('common.errorUpdating')); }
  };

  const handleToggleVisibility = async (ped) => {
    try { await updatePedimento(ped.id, { visible_to_client: !ped.visible_to_client }); loadPedimentos(); }
    catch { toast.error('Error'); }
  };

  // SOIA functions
  const openSoiaDialog = async (ped) => {
    setSoiaPedId(ped.id);
    const num = ped.pedimento_number_manual || ped.pedimento_number || '';
    // Parse pedimento number
    try {
      const res = await soiaLookup({ pedimento_number: num });
      setSoiaParsed(res.data);
    } catch {
      setSoiaParsed({ anio: '', aduana: '430', patente: '', documento: '' });
    }
    // Pre-fill existing movimientos
    if (ped.soia_data?.movimientos?.length > 0) {
      setSoiaMovimientos(ped.soia_data.movimientos);
    } else {
      setSoiaMovimientos([{ situacion: '', detalle: '', fecha: '' }]);
    }
    setSoiaDialogOpen(true);
  };

  const addMovimiento = () => setSoiaMovimientos(prev => [...prev, { situacion: '', detalle: '', fecha: '' }]);
  const removeMovimiento = (i) => setSoiaMovimientos(prev => prev.filter((_, idx) => idx !== i));
  const updateMovimiento = (i, field, val) => setSoiaMovimientos(prev => prev.map((m, idx) => idx === i ? { ...m, [field]: val } : m));

  const saveSoiaData = async () => {
    const validMov = soiaMovimientos.filter(m => m.situacion || m.detalle);
    try {
      await updatePedimento(soiaPedId, {
        soia_data: { movimientos: validMov, detail: {} },
        soia_aduana: soiaParsed.aduana,
        soia_patente: soiaParsed.patente,
        soia_anio: soiaParsed.anio,
        soia_documento: soiaParsed.documento,
        soia_last_check: new Date().toISOString(),
      });
      toast.success('Datos SOIA guardados');
      setSoiaDialogOpen(false);
      loadPedimentos();
    } catch { toast.error('Error al guardar'); }
  };

  const formatSize = (bytes) => bytes ? bytes < 1024 ? `${bytes}B` : bytes < 1024 * 1024 ? `${(bytes / 1024).toFixed(0)}KB` : `${(bytes / (1024 * 1024)).toFixed(1)}MB` : '';

  return (
    <div className="space-y-4" data-testid="pedimento-panel">
      {/* Consultar SOIA ahora (admin) — forces an immediate SAT consult */}
      {isAdmin && (
        <div className="flex items-center justify-between gap-3 bg-muted/30 rounded-lg p-3 border border-border">
          <div className="min-w-0">
            <p className="text-sm font-medium text-foreground">Consulta SOIA (SAT)</p>
            <p className="text-xs text-muted-foreground">Fuerza la consulta del semáforo ahora, sin esperar el horario automático.</p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleConsultSoiaNow}
            disabled={consultingSoia}
            data-testid="consult-soia-now-btn"
          >
            {consultingSoia ? <Loader2 size={14} className="mr-2 animate-spin" /> : <RefreshCw size={14} className="mr-2" />}
            {consultingSoia ? 'Consultando...' : 'Consultar SOIA ahora'}
          </Button>
        </div>
      )}

      {/* Proforma: Extract only (no storage, not visible to client) */}
      {isAdmin && (
        <div className="bg-blue-500/5 rounded-lg p-4 border border-dashed border-blue-500/40">
          <div className="flex items-start gap-2 mb-2">
            <Zap size={16} className="text-blue-400 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-blue-400">Proforma — Solo extraer número</p>
              <p className="text-xs text-muted-foreground">El PDF se procesa en memoria y se descarta. Solo se guarda el número y se asigna al contenedor (o a todos si no eliges uno). No visible al cliente.</p>
            </div>
          </div>
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex-1 min-w-[200px]">
              <Label className="text-xs text-muted-foreground mb-1 block">PDF Proforma</Label>
              <div className="flex items-center gap-2">
                <Input ref={extractFileRef} type="file" accept=".pdf" onChange={handleExtractOnly} disabled={extracting} className="text-sm" data-testid="prepedimento-file-input" />
                {extracting && <Loader2 size={16} className="animate-spin text-blue-400" />}
              </div>
            </div>
            <div className="w-56">
              <Label className="text-xs text-muted-foreground mb-1 block">Asignar a</Label>
              <Select value={extractContainerId || '_all'} onValueChange={v => setExtractContainerId(v === '_all' ? '' : v)}>
                <SelectTrigger data-testid="prepedimento-container-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_all">Todos los contenedores del MBL</SelectItem>
                  {containers.map(c => <SelectItem key={c.id} value={c.id}>{c.container_number}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      )}

      {/* Upload */}
      {isAdmin && (
        <div className="bg-muted/30 rounded-lg p-4 border border-dashed border-border">
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex-1 min-w-[200px]">
              <Label className="text-xs text-muted-foreground mb-1 block">Subir Pedimento PDF</Label>
              <div className="flex items-center gap-2">
                <Input ref={fileInputRef} type="file" accept=".pdf" onChange={handleUpload} disabled={uploading} className="text-sm" data-testid="pedimento-file-input" />
                {uploading && <Loader2 size={16} className="animate-spin text-primary" />}
              </div>
            </div>
            <div className="w-44">
              <Label className="text-xs text-muted-foreground mb-1 block">Contenedor</Label>
              <Select value={uploadContainerId || '_none'} onValueChange={v => setUploadContainerId(v === '_none' ? '' : v)}>
                <SelectTrigger><SelectValue placeholder="Sin asignar" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">Sin asignar</SelectItem>
                  {containers.map(c => <SelectItem key={c.id} value={c.id}>{c.container_number}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <Label className="text-xs text-muted-foreground">Visible cliente</Label>
              <Switch checked={uploadVisible} onCheckedChange={setUploadVisible} />
            </div>
          </div>
        </div>
      )}

      {/* List */}
      {loading ? (
        <div className="flex justify-center py-8"><Loader2 className="animate-spin text-primary" size={24} /></div>
      ) : pedimentos.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground"><FileText size={32} className="mx-auto mb-2 opacity-30" /><p>No hay pedimentos</p></div>
      ) : (
        <div className="space-y-2">
          {pedimentos.map((ped, i) => (
            <div key={ped.id} className="bg-card border border-border rounded-lg p-3" data-testid={`pedimento-item-${i}`}>
              <div className="flex items-center gap-3">
                <FileText size={20} className="text-red-400 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium truncate">{ped.original_filename}</span>
                    <span className="text-xs text-muted-foreground">{formatSize(ped.size)}</span>
                  </div>
                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                    {(ped.pedimento_number_manual || ped.pedimento_number) ? (
                      <Badge variant="default" className="gap-1"><Hash size={10} />{ped.pedimento_number_manual || ped.pedimento_number}</Badge>
                    ) : (<Badge variant="secondary">Sin numero</Badge>)}
                    {ped.visible_to_client ? (
                      <Badge variant="outline" className="text-green-500 border-green-500/30 gap-1"><Eye size={10} />Cliente</Badge>
                    ) : (
                      <Badge variant="outline" className="text-red-400 border-red-400/30 gap-1"><EyeOff size={10} />Oculto</Badge>
                    )}
                    {ped.soia_last_check && (
                      <Badge variant="outline" className="text-blue-400 border-blue-400/30 gap-1"><Search size={10} />SOIA</Badge>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <Button variant="ghost" size="sm" onClick={() => openSoiaDialog(ped)} title="SOIA (SAT)" data-testid={`pedimento-soia-${i}`}>
                    <Search size={14} className="text-blue-400" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => handleDownload(ped)} data-testid={`pedimento-download-${i}`}>
                    <Download size={14} />
                  </Button>
                  {isAdmin && (
                    <>
                      <Button variant="ghost" size="sm" onClick={() => handleToggleVisibility(ped)}>
                        {ped.visible_to_client ? <EyeOff size={14} /> : <Eye size={14} />}
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => { setEditId(ped.id); setEditData({ pedimento_number_manual: ped.pedimento_number_manual || ped.pedimento_number || '', container_id: ped.container_id || '' }); }}>
                        <Edit2 size={14} />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(ped.id)}>
                        <Trash2 size={14} className="text-destructive" />
                      </Button>
                    </>
                  )}
                </div>
              </div>
              {/* SOIA Timeline (auto-scraper data: pagado, semáforo, reconocimiento, desanuadizado) */}
              <SoiaTimeline
                soiaData={ped.soia_data}
                lastCheck={ped.soia_data_updated_at || ped.soia_last_check}
              />

              {/* SOIA Movimientos (legacy manual movements) */}
              {ped.soia_data?.movimientos?.length > 0 && (
                <div className="mt-2 ml-8 border-l-2 border-blue-500/20 pl-3">
                  <p className="text-xs font-medium text-blue-400 mb-1 flex items-center gap-1"><Clock size={10} />Movimientos SOIA</p>
                  {ped.soia_data.movimientos.map((mov, mi) => (
                    <div key={mi} className="flex items-start gap-2 text-xs py-0.5">
                      <span className="text-muted-foreground w-28 flex-shrink-0 font-mono">{mov.fecha || ''}</span>
                      <span className="font-medium">{mov.situacion || ''}</span>
                      {mov.detalle && <span className="text-muted-foreground">- {mov.detalle}</span>}
                    </div>
                  ))}
                  {ped.soia_last_check && (
                    <p className="text-xs text-muted-foreground mt-1">Ultima consulta: {new Date(ped.soia_last_check).toLocaleDateString('es-MX')}</p>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Edit Dialog */}
      <Dialog open={!!editId} onOpenChange={o => !o && setEditId(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Editar Pedimento</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Numero de Pedimento</Label>
              <Input value={editData.pedimento_number_manual || ''} onChange={e => setEditData(p => ({ ...p, pedimento_number_manual: e.target.value }))} placeholder="25 43 3920 6000191" className="font-mono" data-testid="edit-pedimento-number" /></div>
            <div><Label>Contenedor</Label>
              <Select value={editData.container_id || '_none'} onValueChange={v => setEditData(p => ({ ...p, container_id: v === '_none' ? '' : v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="_none">Sin asignar</SelectItem>{containers.map(c => <SelectItem key={c.id} value={c.id}>{c.container_number}</SelectItem>)}</SelectContent>
              </Select></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditId(null)}>Cancelar</Button>
            <Button onClick={handleSaveEdit}><Check size={14} className="mr-1" />Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* SOIA Dialog */}
      <Dialog open={soiaDialogOpen} onOpenChange={setSoiaDialogOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Search size={16} className="text-blue-400" />SOIA (SAT) - Consulta de Pedimento
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Parsed pedimento info */}
            <div className="grid grid-cols-4 gap-2 bg-secondary/30 rounded p-2.5">
              <div className="text-center">
                <span className="text-[10px] text-muted-foreground block">Ano</span>
                <span className="font-mono font-medium text-sm">{soiaParsed.anio || '-'}</span>
              </div>
              <div className="text-center">
                <span className="text-[10px] text-muted-foreground block">Aduana</span>
                <span className="font-mono font-medium text-sm">{soiaParsed.aduana || '-'}</span>
              </div>
              <div className="text-center">
                <span className="text-[10px] text-muted-foreground block">Patente</span>
                <span className="font-mono font-medium text-sm">{soiaParsed.patente || '-'}</span>
              </div>
              <div className="text-center">
                <span className="text-[10px] text-muted-foreground block">Documento</span>
                <span className="font-mono font-medium text-sm">{soiaParsed.documento || '-'}</span>
              </div>
            </div>

            {/* Link to SOIA */}
            <a href={SOIA_URL} target="_blank" rel="noopener noreferrer"
              className="flex items-center gap-2 p-3 rounded border border-blue-500/30 bg-blue-500/5 hover:bg-blue-500/10 transition-colors">
              <ExternalLink size={16} className="text-blue-400" />
              <div>
                <p className="text-sm font-medium text-blue-400">Abrir SOIA en nueva pestana</p>
                <p className="text-xs text-muted-foreground">Ingresa los datos de arriba en el formulario del SAT</p>
              </div>
            </a>

            {/* Manual Movimientos Entry */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-sm font-medium">Movimientos del Pedimento</Label>
                <Button variant="outline" size="sm" onClick={addMovimiento} className="h-7 text-xs">
                  <Plus size={12} className="mr-1" />Agregar
                </Button>
              </div>
              <div className="space-y-2">
                {soiaMovimientos.map((mov, i) => (
                  <div key={i} className="flex items-start gap-2 p-2 rounded border border-border/50 bg-muted/10">
                    <div className="grid grid-cols-3 gap-2 flex-1">
                      <Input value={mov.fecha} onChange={e => updateMovimiento(i, 'fecha', e.target.value)}
                        placeholder="dd/mm/aaaa hh:mm" className="h-7 text-xs font-mono" />
                      <Input value={mov.situacion} onChange={e => updateMovimiento(i, 'situacion', e.target.value)}
                        placeholder="SITUACION" className="h-7 text-xs" />
                      <Input value={mov.detalle} onChange={e => updateMovimiento(i, 'detalle', e.target.value)}
                        placeholder="DETALLE" className="h-7 text-xs" />
                    </div>
                    {soiaMovimientos.length > 1 && (
                      <button onClick={() => removeMovimiento(i)} className="text-destructive hover:bg-destructive/10 p-1 rounded mt-0.5">
                        <X size={12} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
              <p className="text-xs text-muted-foreground mt-1">Copia los movimientos del SOIA aqui para tener un registro</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSoiaDialogOpen(false)}>Cancelar</Button>
            <Button onClick={saveSoiaData} data-testid="soia-save-btn">
              <Check size={14} className="mr-1" />Guardar Datos SOIA
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
